import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { calculateTakeHomePay } from '../utils/taxCalculations';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { updateMetaTags, defaultMeta } from '../utils/metaTags';

const { FiInfo } = FiIcons;

const TakeHomeTable = () => {
  useEffect(() => {
    updateMetaTags({
      title: 'UK Take-Home Pay Table 2025/26 | £20k - £200k Salary Comparison',
      description: 'Compare monthly take-home pay across different UK salary levels (£20,000 - £200,000) for the 2025/26 tax year. Includes England, Wales, Scotland & Northern Ireland.',
      keywords: 'UK salary table, take-home pay table, salary comparison, UK tax table, monthly take-home pay, salary after tax table',
      ogTitle: 'UK Take-Home Pay Table 2025/26 (£20k - £200k)',
      ogDescription: 'Compare monthly take-home pay across different UK salary levels for the 2025/26 tax year.',
      canonical: `${defaultMeta.siteUrl}/take-home-table`,
    });
  }, []);

  const [region, setRegion] = useState('england');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Extended range from 20,000 to 200,000
  const generateTableData = () => {
    const data = [];
    for (let salary = 20000; salary <= 200000; salary += 5000) {
      const result = calculateTakeHomePay({
        annualSalary: salary,
        region,
        pensionPercent: 0,
        studentLoanPlan: 'none'
      });
      
      data.push({
        salary,
        monthly: result.netPay.monthly,
        tax: result.incomeTax,
        ni: result.nationalInsurance,
        percent: ((result.incomeTax + result.nationalInsurance) / salary) * 100
      });
    }
    return data;
  };

  const tableData = generateTableData();

  return (
    <div className="space-y-8">
      <motion.section 
        className="text-center space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <span className="px-4 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-black uppercase tracking-widest">Full Comparison</span>
        <h1 className="text-4xl md:text-5xl font-black text-slate-900">
          UK Take-Home Pay Table <span className="text-emerald-600">£20k - £200k</span>
        </h1>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          Compare monthly take-home pay across different salary levels for the 2025/26 tax year.
        </p>
      </motion.section>

      <motion.section 
        className="flex justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        <div className="bg-white rounded-2xl p-2 shadow-xl border border-slate-100 inline-flex">
          <button
            onClick={() => setRegion('england')}
            className={`px-8 py-3 rounded-xl font-black transition-all ${
              region === 'england'
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            UK (excl. Scot)
          </button>
          <button
            onClick={() => setRegion('scotland')}
            className={`px-8 py-3 rounded-xl font-black transition-all ${
              region === 'scotland'
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Scotland
          </button>
        </div>
      </motion.section>

      <motion.section 
        className="bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-900">
                <th className="px-6 py-5 text-left text-xs font-black text-slate-400 uppercase tracking-widest">Annual Salary</th>
                <th className="px-6 py-5 text-left text-xs font-black text-slate-400 uppercase tracking-widest">Monthly Net</th>
                <th className="px-6 py-5 text-left text-xs font-black text-slate-400 uppercase tracking-widest">Annual Tax</th>
                <th className="px-6 py-5 text-left text-xs font-black text-slate-400 uppercase tracking-widest">Annual NI</th>
                <th className="px-6 py-5 text-left text-xs font-black text-slate-400 uppercase tracking-widest">Tax %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tableData.map((row, index) => (
                <tr 
                  key={row.salary} 
                  className={`hover:bg-blue-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                >
                  <td className="px-6 py-5 whitespace-nowrap text-sm font-black text-slate-900">
                    {formatCurrency(row.salary)}
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap text-sm font-black text-emerald-600">
                    {formatCurrency(row.monthly)}
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap text-sm font-bold text-rose-500">
                    {formatCurrency(row.tax)}
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap text-sm font-bold text-amber-500">
                    {formatCurrency(row.ni)}
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-600" 
                          style={{ width: `${row.percent}%` }}
                        />
                      </div>
                      <span className="text-xs font-bold text-slate-500">{row.percent.toFixed(1)}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.section>

      {/* Comprehensive Analysis Section */}
      <motion.section 
        className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-slate-200 space-y-8"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Understanding Your Take-Home Pay: Complete Analysis</h2>
          <p className="text-gray-700 leading-relaxed">
            This comprehensive table shows how take-home pay changes across different salary levels. Understanding these patterns helps with financial planning, salary negotiations, and tax optimization strategies.
          </p>
        </div>

        <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
          <h3 className="font-semibold text-gray-900 mb-4 text-lg">Key Patterns in the Table</h3>
          <div className="grid md:grid-cols-2 gap-6 text-sm text-gray-700">
            <div>
              <h4 className="font-medium mb-2 text-blue-900">£20,000 - £50,000 Range:</h4>
              <ul className="space-y-1 leading-relaxed">
                <li>• Basic rate tax (20%) applies to most of this range</li>
                <li>• National Insurance at 8% on earnings above £12,570</li>
                <li>• Effective tax rate: approximately 20-25%</li>
                <li>• Take-home pay increases steadily with salary</li>
                <li>• This is the most common salary range in the UK</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2 text-blue-900">£50,000 - £100,000 Range:</h4>
              <ul className="space-y-1 leading-relaxed">
                <li>• Higher rate tax (40%) kicks in above £50,270</li>
                <li>• NI rate drops to 2% above £50,270</li>
                <li>• Effective tax rate: approximately 30-35%</li>
                <li>• Significant jump in tax burden at £50,270 threshold</li>
                <li>• Pension contributions become more tax-efficient</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 rounded-xl p-6 border border-amber-100">
          <h3 className="font-semibold text-gray-900 mb-4 text-lg">The £100,000 Tax Trap Explained</h3>
          <p className="text-gray-700 mb-4 text-sm leading-relaxed">
            As you can see in the table, the effective tax rate increases dramatically above £100,000. This is due to the Personal Allowance Taper, which creates a "60% tax trap" on earnings between £100,000 and £125,140.
          </p>
          <div className="bg-white rounded-lg p-4 border border-amber-200 text-sm">
            <h4 className="font-medium mb-2 text-amber-900">How the Taper Works:</h4>
            <ul className="space-y-2 text-gray-700 leading-relaxed">
              <li>• <strong>Below £100,000:</strong> Full personal allowance of £12,570</li>
              <li>• <strong>£100,000 - £125,140:</strong> Personal allowance reduced by £1 for every £2 earned</li>
              <li>• <strong>At £125,140:</strong> Personal allowance completely eliminated</li>
              <li>• <strong>Result:</strong> Effective tax rate of 60% on this band (40% income tax + 2% NI + loss of personal allowance)</li>
            </ul>
            <p className="mt-3 text-amber-800 font-medium">
              <strong>Strategy:</strong> Many high earners increase pension contributions to stay below £100,000, effectively saving 60% tax on those contributions.
            </p>
          </div>
        </div>

        <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-100">
          <h3 className="font-semibold text-gray-900 mb-4 text-lg">Scotland vs Rest of UK: Tax Differences</h3>
          <p className="text-gray-700 mb-4 text-sm leading-relaxed">
            Scottish taxpayers face different income tax rates. Use the toggle above to compare. Generally, Scottish taxpayers pay more tax on middle incomes (£26,000 - £43,000) but similar rates on very high incomes.
          </p>
          <div className="bg-white rounded-lg p-4 border border-emerald-200 text-sm">
            <h4 className="font-medium mb-2 text-emerald-900">Key Scottish Tax Differences:</h4>
            <ul className="space-y-2 text-gray-700 leading-relaxed">
              <li>• <strong>Starter rate (19%):</strong> £12,571 - £14,876 (not in rest of UK)</li>
              <li>• <strong>Intermediate rate (21%):</strong> £26,562 - £43,662 (not in rest of UK)</li>
              <li>• <strong>Higher rate threshold:</strong> £43,662 (vs £50,270 in rest of UK) - starts earlier</li>
              <li>• <strong>Higher rate:</strong> 42% (vs 40% in rest of UK) - 2% higher</li>
              <li>• <strong>Result:</strong> Scottish taxpayers earning £30,000-£50,000 typically pay more tax</li>
            </ul>
          </div>
        </div>

        <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-100">
          <h3 className="font-semibold text-gray-900 mb-4 text-lg">Tax Optimization Strategies</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
            <div>
              <h4 className="font-medium mb-2 text-indigo-900">For Basic Rate Taxpayers (£12,570 - £50,270):</h4>
              <ul className="space-y-1 leading-relaxed">
                <li>• Maximize employer pension contributions</li>
                <li>• Use tax-free ISA allowances (£20,000 per year)</li>
                <li>• Consider salary sacrifice schemes</li>
                <li>• Claim all eligible tax reliefs</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2 text-indigo-900">For Higher Rate Taxpayers (£50,270+):</h4>
              <ul className="space-y-1 leading-relaxed">
                <li>• Pension contributions get 40% tax relief (or 45% if additional rate)</li>
                <li>• Consider staying below £100,000 to avoid taper</li>
                <li>• Maximize pension contributions (up to £60,000 annual allowance)</li>
                <li>• Use salary sacrifice for benefits (saves NI too)</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <h3 className="font-semibold text-gray-900 mb-4 text-lg">Understanding the Tax Percentage Column</h3>
          <p className="text-gray-700 mb-4 text-sm leading-relaxed">
            The "Tax %" column shows the combined Income Tax and National Insurance as a percentage of your gross salary. This gives you a clear view of your overall tax burden at different salary levels.
          </p>
          <div className="bg-white rounded-lg p-4 border border-slate-300 text-sm">
            <h4 className="font-medium mb-2">What the Percentage Means:</h4>
            <ul className="space-y-2 text-gray-700 leading-relaxed">
              <li>• <strong>20-25%:</strong> Typical for salaries £20,000-£50,000 (basic rate taxpayers)</li>
              <li>• <strong>30-35%:</strong> Typical for salaries £50,000-£100,000 (higher rate taxpayers)</li>
              <li>• <strong>40-45%:</strong> Typical for salaries £100,000-£150,000 (affected by personal allowance taper)</li>
              <li>• <strong>45%+:</strong> Very high earners (£150,000+) paying additional rate tax</li>
            </ul>
            <p className="mt-3 text-xs text-gray-600 italic">
              Note: These percentages don't include student loan repayments, pension contributions, or other deductions. The actual percentage of take-home pay will be lower when these are factored in.
            </p>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default TakeHomeTable;